// Task "parts" are the discrete items on a task row a sharee can comment on (Title, Duration,
// Frequency, UoM, Skill, Classification). The schedule task components mark each item's element
// with `data-comment-part="<part>"` (a literal marker, like data-cy, so the shared task components
// don't import from this feature). Everything that reads those markers lives here, kept free of
// React/MUI so it is unit-testable against plain DOM elements.

export const SHARE_LINK_COMMENT_PART_ATTRIBUTE = "data-comment-part"
// Set on a marked element while a saved comment (or the composer's draft target) anchors to it —
// value is the highlight emphasis ("selected" | "muted" | "draft"). Styling keys off this attribute
// rather than the text-highlight class: clearTextHighlights unwraps elements carrying that class,
// which would destroy the field element itself.
export const SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE = "data-comment-part-highlight"
export const SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE = "data-comment-part-comment-id"

export const SHARE_LINK_COMMENT_PART_LABELS = {
    title: "Title",
    time: "Duration",
    frequency: "Frequency",
    uom: "UoM",
    skill: "Skill",
    classification: "Classification",
}

export function getShareLinkCommentPartLabel(part) {
    if (!part) return null
    return SHARE_LINK_COMMENT_PART_LABELS[part] ?? null
}

export function getShareLinkCommentTaskPartLabel(part) {
    const label = getShareLinkCommentPartLabel(part)
    if (!label) return null
    return `Task ${label}`
}

/**
 * Walk up from a node (text node or element) to the nearest marked part element.
 * @param {Node|null} node
 * @returns {Element|null}
 */
export function findCommentPartElement(node) {
    const element = toElement(node)
    return element?.closest?.(`[${SHARE_LINK_COMMENT_PART_ATTRIBUTE}]`) ?? null
}

/**
 * Resolve the part key a node sits inside, or null when it is not in a known part.
 * @param {Node|null} node
 * @returns {string|null}
 */
export function getCommentPartFromNode(node) {
    const part = findCommentPartElement(node)?.getAttribute(SHARE_LINK_COMMENT_PART_ATTRIBUTE) ?? null
    if (!getShareLinkCommentPartLabel(part)) return null
    return part
}

/**
 * The captured value is what the sharee was looking at: prefer the rendered text of the marked
 * element, falling back to the task's data for icon-only items (classification renders a glyph).
 * @param {{ task?: object, part: string, element?: Element|null }} params
 * @returns {string}
 */
export function getCommentPartValue({ task, part, element }) {
    const displayed = normalizeWhitespace(element?.textContent)
    if (displayed) return displayed
    return getTaskDataValue()

    function getTaskDataValue() {
        if (part === "title") return normalizeWhitespace(stripHtml(task?.title))
        if (part === "time") return formatMinutes(task?.minutes)
        if (part === "frequency") return formatFrequency(task?.frequency)
        if (part === "uom") return normalizeWhitespace(task?.unitOfMeasure)
        if (part === "skill") return normalizeWhitespace(task?.skillSet)
        if (part === "classification") return normalizeWhitespace(task?.classification)
        return ""
    }
}

/**
 * Find the marked element for a part within a task row, used to attach highlight attributes.
 * @param {Element|null} rowElement
 * @param {string} part
 * @returns {Element|null}
 */
export function findPartElementInRow(rowElement, part) {
    if (!rowElement || !part) return null
    return rowElement.querySelector(`[${SHARE_LINK_COMMENT_PART_ATTRIBUTE}="${part}"]`)
}

/**
 * Mark a part element as highlighted for a saved comment or composer draft.
 * @returns {boolean} whether a matching element was found and marked
 */
export function markPartHighlight(rowElement, part, { commentId, emphasis } = {}) {
    const element = findPartElementInRow(rowElement, part)
    if (!element) return false
    element.setAttribute(SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE, emphasis ?? "selected")
    if (commentId) element.setAttribute(SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE, commentId)
    return true
}

/**
 * Remove every part highlight marker under a row, restoring the plain markers.
 * @param {Element|null} rowElement
 */
export function clearPartHighlights(rowElement) {
    if (!rowElement) return
    const marked = rowElement.querySelectorAll(`[${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}]`)
    for (const element of marked) {
        element.removeAttribute(SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE)
        element.removeAttribute(SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE)
    }
}

/**
 * Given a click target, resolve the comment id of the part highlight it sits inside (if any), so
 * clicking a commented field opens its thread, mirroring resolveClickedCommentId for text spans.
 * @param {EventTarget|null} target
 * @returns {string|null}
 */
export function resolveClickedPartCommentId(target) {
    const element = findCommentPartElement(target)
    return element?.getAttribute(SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE) ?? null
}

/**
 * The part a saved comment is anchored to, reading the explicit field with a fallback to the third
 * path segment (["tasks", "<index>", "<part>"]) so anchors survive consumers that strip fields.
 * @param {{ anchor?: { part?: string, path?: string[] } }|null} comment
 * @returns {string|null}
 */
export function getCommentAnchorPart(comment) {
    const anchor = comment?.anchor ?? {}
    const part = anchor.part ?? (anchor.path?.[0] === "tasks" ? anchor.path?.[2] : null)
    if (!getShareLinkCommentPartLabel(part)) return null
    return part
}

function toElement(node) {
    if (!node) return null
    if (node.nodeType === 1) return node
    return node.parentElement ?? null
}

function normalizeWhitespace(value) {
    return `${value ?? ""}`.replace(/\s+/g, " ").trim()
}

function stripHtml(value) {
    return `${value ?? ""}`.replace(/<[^>]+>/g, " ")
}

function formatMinutes(minutes) {
    if (!minutes) return ""
    return `${minutes} min`
}

function formatFrequency(frequency) {
    if (!frequency?.interval || !frequency?.period) return ""
    return `Every ${frequency.interval}${frequency.period}`
}
